import React from 'react';

import Cookies from 'js-cookie';
import { fetchQuery, graphql, useRelayEnvironment } from 'react-relay';

import logger from 'src/clients/LoggingClient';
import { NavigationProviderClientContextQuery as NavigationProviderClientContextQueryType } from 'src/clients/Network/gql/build/NavigationProviderClientContextQuery.graphql';
import {
  CreditApplicationPageType,
  NavigationProviderCreditApplicationPageQuery as NavigationProviderCreditApplicationPageQueryType,
} from 'src/clients/Network/gql/build/NavigationProviderCreditApplicationPageQuery.graphql';
import { NavigationProviderIdentityQuery as NavigationProviderIdentityQueryType } from 'src/clients/Network/gql/build/NavigationProviderIdentityQuery.graphql';
import { NavigationProviderUnderwriteMutation as NavigationProviderUnderwriteMutationType } from 'src/clients/Network/gql/build/NavigationProviderUnderwriteMutation.graphql';
import { AddressType } from 'src/clients/Network/gql/types/address';
import { Domain, fetch } from 'src/clients/Network/http/fetch';
import { trackPageView } from 'src/clients/tracking';
import { getAuthState } from 'src/state/auth/auth';
import { useGlobalStore } from 'src/state/global';
import { AddressTypeToAddressInput } from 'src/utils/address';
import { useAsyncMutation } from 'src/utils/graphql';
import AccountAlreadyExistsPage from 'src/views/creditApplication/AccountAlreadyExistsPage';
import AddressChallengePage from 'src/views/creditApplication/AddressChallengePage';
import AdverseActionPage from 'src/views/creditApplication/AdverseActionPage';
import ApplicationReviewPage from 'src/views/creditApplication/ApplicationReviewPage';
import ApplicationUnavailablePage from 'src/views/creditApplication/ApplicationUnavailablePage';
import CreditFrozenPage from 'src/views/creditApplication/CreditFrozenPage';
import CreditLimitRevealPage from 'src/views/creditApplication/CreditLimitRevealPage';
import DownloadAppPage from 'src/views/creditApplication/DownloadAppPage';
import IDVFailPage from 'src/views/creditApplication/IDVFailPage';
import IncomePage from 'src/views/creditApplication/IncomePage';
import LandingPage from 'src/views/creditApplication/LandingPage';
import ResidentialAddressRouter from 'src/views/creditApplication/ResidentialAddress';
import ShippingAddressRouter from 'src/views/creditApplication/ShippingAddress';
import TermsAndConditionsPage from 'src/views/creditApplication/TermsAndConditionsPage';
import UnknownErrorPage from 'src/views/creditApplication/UnknownErrorPage';
import UserCreationPage from 'src/views/creditApplication/UserCreationPage';
import FinalTermsRouter from 'src/views/creditApplication/finalTerms/FinalTermsRouter';
import FraudAlertRouter from 'src/views/creditApplication/fraudAlert/FraudAlertRouter';
import PersonalInformationPage from 'src/views/creditApplication/personalInformation/PersonalInformationPage';
import SSNVerifiedPage from 'src/views/creditApplication/ssnVerification/SSNVerifiedPage';
import VerifySSNPage from 'src/views/creditApplication/ssnVerification/VerifySSNPage';

// Campaign-specific cookie keys
export const CkSubIdCookieKey = 'x1_ck_sub_id';
export const CkBadgeCookieKey = 'x1_ck_badge';
export const CkPayloadCookieKey = 'x1_ck_payload';
export const CkOfferVariantCookieKey = 'x1_ck_offer_variant';
export const X1InviteCookieKey = 'x1_invite_slug';

// Generic campaign cookie keys
export const InviteCookieKey = 'invite';
export const UtmSourceCookieKey = 'utm_source';
export const UtmMediumCookieKey = 'utm_medium';
export const UtmCampaignCookieKey = 'utm_campaign';
export const UtmContentCookieKey = 'utm_content';
export const UtmTermCookieKey = 'utm_term';

const NavigationProviderClientContextQuery = graphql`
  query NavigationProviderClientContextQuery {
    mobileClientContext {
      webApplicationAvailable
    }
  }
`;

const NavigationProviderCreditApplicationPageQuery = graphql`
  query NavigationProviderCreditApplicationPageQuery($q: CreditApplicationPageRequest!) {
    creditApplicationPage(q: $q) {
      page
    }
  }
`;

const NavigationProviderIdentityQuery = graphql`
  query NavigationProviderIdentityQuery {
    authIdentity {
      id
      creditApplications {
        id
        createdAt
      }
      settings {
        residentialAddress {
          id
          streetAddress
          primaryAddressLine
          secondaryAddressLine
          locality
          subdivision
          zip
        }
      }
    }
  }
`;

const NavigationProviderUnderwriteMutation = graphql`
  mutation NavigationProviderUnderwriteMutation($q: UnderwriteRequest!) {
    underwriteApplication(q: $q) {
      data {
        creditApplication {
          id
        }
      }
    }
  }
`;

export type PageType =
  | CreditApplicationPageType
  | 'LANDING'
  | 'USER_CREATION'
  | 'PERSONAL_INFORMATION'
  | 'RESIDENTIAL_ADDRESS'
  | 'INCOME'
  | 'SHIPPING_ADDRESS_ENTRY'
  | 'ACCOUNT_ALREADY_EXISTS'
  | 'UNAVAILABLE';

const ClientSidePageTypes: PageType[] = [
  'LANDING',
  'USER_CREATION',
  'PERSONAL_INFORMATION',
  'RESIDENTIAL_ADDRESS',
  'INCOME',
  'UNAVAILABLE',
];

export const PageComponentMap: Partial<Record<PageType, React.ComponentType>> = {
  LANDING: LandingPage,
  USER_CREATION: UserCreationPage,
  SSN_VERIFICATION_PROMPT: VerifySSNPage,
  SSN_VERIFIED_PROMPT: SSNVerifiedPage,
  DOWNLOAD_APP_NOTICE: DownloadAppPage,
  ADDRESS_CHALLENGE_ENTRY: AddressChallengePage,
  IDENTITY_CONTEST_ENTRY: IDVFailPage,
  CREDIT_FREEZE_PROMPT: CreditFrozenPage,
  FRAUD_ALERT_PHONE_PROMPT: FraudAlertRouter,
  FRAUD_ALERT_MANUAL_REVIEW_PROMPT: FraudAlertRouter,
  APPLICATION_REVIEW_NOTICE: ApplicationReviewPage,
  FINAL_TERMS_PROMPT: FinalTermsRouter,
  RUN_ELIGIBILITY_PROMPT: TermsAndConditionsPage,
  LIMIT_REVEAL_PROMPT: CreditLimitRevealPage,
  ADVERSE_ACTION_NOTICE: AdverseActionPage,
  UNKNOWN_ERROR_NOTICE: UnknownErrorPage,
  PERSONAL_INFORMATION: PersonalInformationPage,
  INCOME: IncomePage,
  ACCOUNT_ALREADY_EXISTS: AccountAlreadyExistsPage,
  RESIDENTIAL_ADDRESS: ResidentialAddressRouter,
  SHIPPING_ADDRESS_ENTRY: ShippingAddressRouter,
  UNAVAILABLE: ApplicationUnavailablePage,
};

const PageTypeToTitleCase = (str: string): string => {
  return str
    .toLowerCase()
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join('');
};

interface NavigationContextType {
  nextPage: () => Promise<void>;
  nextPageLoading: boolean;
  underwrite: (creditApplicationIdOverride?: string) => Promise<void>;
  underwriteLoading: boolean;
  currentPage?: PageType;
  setCurrentPage: (page: PageType) => void;
}

const NavigationContext = React.createContext<NavigationContextType>({
  nextPage: () => Promise.resolve(),
  nextPageLoading: false,
  underwrite: (creditApplicationIdOverride?: string) => Promise.resolve(),
  underwriteLoading: false,
  setCurrentPage: () => {},
});

const NavigationProvider = (props: React.PropsWithChildren<{}>) => {
  const [underwriteCommit] = useAsyncMutation<NavigationProviderUnderwriteMutationType>(
    NavigationProviderUnderwriteMutation,
  );
  const creditApplicationId = useGlobalStore((s) => s.creditApplicationId);
  const [currentPage, setCurrentPage] = React.useState<PageType | undefined>();
  const [nextPageLoading, setNextPageLoading] = React.useState<boolean>(false);
  const [underwriteLoading, setUnderwriteLoading] = React.useState<boolean>(false);
  const currentPageRef = React.useRef<PageType | undefined>(undefined);
  const relayEnv = useRelayEnvironment();

  React.useEffect(() => {
    if (currentPage) {
      trackPageView(PageTypeToTitleCase(currentPage));
    }
  }, [currentPage]);

  const _nextPage = React.useCallback(async () => {
    const setPage = (page: PageType) => {
      currentPageRef.current = page;
      setCurrentPage(page);
    };

    const x1InviteCookie = Cookies.get(X1InviteCookieKey);
    const ckSubIdCookie = Cookies.get(CkSubIdCookieKey);
    const campaignCookie = Cookies.get(UtmCampaignCookieKey);

    // Skip affiliate web application check if campaign cookie is present
    if (!x1InviteCookie && !campaignCookie) {
      try {
        const clientContext = await fetchQuery<NavigationProviderClientContextQueryType>(
          relayEnv,
          NavigationProviderClientContextQuery,
          {},
          { fetchPolicy: 'network-only' },
        ).toPromise();

        if (!clientContext?.mobileClientContext?.webApplicationAvailable) {
          setPage('UNAVAILABLE');
          setNextPageLoading(false);
          return;
        }
      } catch (e) {
        logger.error(e, {
          source: 'NavigationProvider.NavigationProviderClientContextQuery',
        });
      }
    }

    const authState = getAuthState();
    if (!authState) {
      if (!ckSubIdCookie && !x1InviteCookie && !campaignCookie) {
        setPage('UNKNOWN_ERROR_NOTICE');
      } else {
        setPage('LANDING');
      }
      setNextPageLoading(false);
      return;
    }

    let identityId: string | undefined;
    let creditApplicationId: string | undefined;
    let shippingAddress: AddressType | undefined;
    try {
      const response = await fetchQuery<NavigationProviderIdentityQueryType>(
        relayEnv,
        NavigationProviderIdentityQuery,
        {},
        { fetchPolicy: 'network-only' },
      ).toPromise();
      if (response?.authIdentity.id != null) {
        identityId = response.authIdentity.id;
        // Get latest credit application
        creditApplicationId = [...response.authIdentity.creditApplications]
          .sort((a, b) => b.createdAt - a.createdAt)
          .at(0)?.id;
        shippingAddress = response.authIdentity.settings?.residentialAddress ?? undefined;
      }
    } catch (e) {
      // NOTE (szhang): Assuming that if the query fails, then there is no identity id
      fetch(Domain.RHCreditCard, '/auth/exchange/', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
      }).then((res) => {
        if (res.ok) {
          res.json().then((data) => {
            if (data.identityId != null) {
              identityId = data.identityId;
              setPage('PERSONAL_INFORMATION');
              return;
            }
          });
        }
      });
    } finally {
      if (!creditApplicationId && !ckSubIdCookie && !x1InviteCookie && !campaignCookie) {
        setPage('UNKNOWN_ERROR_NOTICE');
        setNextPageLoading(false);
        return;
      }
      useGlobalStore.setState({
        identityId,
        creditApplicationId,
        personalInformation: {
          ...useGlobalStore.getState().personalInformation,
          shippingAddress: AddressTypeToAddressInput(shippingAddress),
        },
        ckSubId: ckSubIdCookie,
        ckBadge: Cookies.get(CkBadgeCookieKey),
        ckPayload: Cookies.get(CkPayloadCookieKey),
        ckOfferVariant: Cookies.get(CkOfferVariantCookieKey),
        x1Invite: x1InviteCookie,
      });
    }

    if (identityId == null) {
      setPage('LANDING');
      return;
    }

    if (creditApplicationId == null) {
      switch (currentPageRef.current) {
        case 'PERSONAL_INFORMATION':
          setPage('RESIDENTIAL_ADDRESS');
          return;
        case 'RESIDENTIAL_ADDRESS':
          setPage('INCOME');
          return;
        case 'INCOME':
          setPage('RUN_ELIGIBILITY_PROMPT');
          return;
        case 'LANDING':
        case 'USER_CREATION':
        default:
          setPage('PERSONAL_INFORMATION');
          return;
      }
    }

    try {
      const pageData = await fetchQuery<NavigationProviderCreditApplicationPageQueryType>(
        relayEnv,
        NavigationProviderCreditApplicationPageQuery,
        {
          q: {
            creditApplicationId,
            currPage:
              currentPageRef.current != null && !ClientSidePageTypes.includes(currentPageRef.current)
                ? (currentPageRef.current as CreditApplicationPageType)
                : undefined,
          },
        },
        { fetchPolicy: 'network-only' },
      ).toPromise();
      if (pageData?.creditApplicationPage.page != null) {
        setPage(pageData.creditApplicationPage.page);
      } else {
        setPage('UNKNOWN_ERROR_NOTICE');
      }
    } catch (e) {
      logger.error(e, {
        source: 'NavigationProvider.NavigationProviderCreditApplicationPageQuery',
        creditApplicationId,
      });
      setPage('UNKNOWN_ERROR_NOTICE');
      return;
    }
  }, []);

  const nextPage = React.useCallback(async () => {
    setNextPageLoading(true);
    await _nextPage();
    setNextPageLoading(false);
  }, [_nextPage]);

  const underwrite = React.useCallback(
    async (creditApplicationIdOverride?: string) => {
      setUnderwriteLoading(true);
      try {
        if (!!creditApplicationId || !!creditApplicationIdOverride) {
          await underwriteCommit({
            variables: {
              q: {
                creditApplicationId: creditApplicationIdOverride ?? (creditApplicationId as string),
              },
            },
          });
        }
      } catch (e) {
        logger.error(e, {
          source: 'NavigationProvider.underwrite',
          creditApplicationId: creditApplicationIdOverride ?? creditApplicationId,
        });
      } finally {
        await nextPage();
        setUnderwriteLoading(false);
      }
    },
    [creditApplicationId, nextPage],
  );

  React.useEffect(() => {
    nextPage();
  }, [nextPage]);

  const context: NavigationContextType = React.useMemo(() => {
    return { nextPage, underwrite, setCurrentPage, currentPage, nextPageLoading, underwriteLoading };
  }, [nextPage, underwrite, setCurrentPage, currentPage, nextPageLoading, underwriteLoading]);

  return <NavigationContext.Provider value={context}>{props.children}</NavigationContext.Provider>;
};

export { NavigationContext, NavigationProvider };
